import { jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LOCAL_FEATURE_FLAGS } from "./feature-flags-helper.js";
import { getFeatureFlagOverridesFromEnv, getURLParamsFeatureFlagOverrides } from "./feature-flag-overrides.js";
import { DEFAULT_CLIENT_FEATURE_FLAGS, FEATURE_FLAG_QUERY_VARIABLES, mapFeatureFlagResponse } from "./feature-flags-utils.js";
import { logError } from "../errors/index.js";
import { useIsAuthenticated, useIsInternalUser } from "../authentication/index.js";
import { gql, useQuery } from "../graphql/index.js";
import { GetFeatureFlagsV2Context } from "../generated/graphql.js";
import { useLocalStorage } from "../browser-storage/index.js";
const DEFAULT_FEATURE_FLAGS = {
    ...DEFAULT_CLIENT_FEATURE_FLAGS,
    ...LOCAL_FEATURE_FLAGS
};
const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlagsV2($flags: [FeatureFlagRequest!]!) {
    featureFlagsV2(flags: $flags) {
      key
      value {
        ... on BoolValue {
          boolValue: value
        }
        ... on StringValue {
          stringValue: value
        }
        ... on IntValue {
          intValue: value
        }
      }
    }
  }
`;
const FeatureFlagContext = /*#__PURE__*/ createContext({
    isLoading: false,
    originalFeatureFlags: DEFAULT_FEATURE_FLAGS,
    featureFlags: DEFAULT_FEATURE_FLAGS,
    setFeatureFlags: ()=>{},
    reset: ()=>{}
});
function FeatureFlagContextProvider(props) {
    const isAuthenticated = useIsAuthenticated();
    const [searchParams] = useSearchParams();
    const [{ data, error, fetching }] = useQuery({
        query: GET_FEATURE_FLAGS,
        context: GetFeatureFlagsV2Context,
        variables: FEATURE_FLAG_QUERY_VARIABLES,
        pause: !isAuthenticated
    });
    useEffect(()=>{
        if (null == error) return;
        logError(error);
    }, [
        error
    ]);
    const flags = {
        ...mapFeatureFlagResponse(data),
        ...LOCAL_FEATURE_FLAGS
    };
    const isChronoAdmin = useIsInternalUser();
    const envConfigLocalOverrides = getFeatureFlagOverridesFromEnv();
    const [localStorageFeatureFlags, setLocalStorageFeatureFlags] = useLocalStorage('featureFlagsOverrides', {});
    useEffect(()=>{
        const paramOverrides = getURLParamsFeatureFlagOverrides(searchParams.toString());
        if (Object.keys(paramOverrides).length > 0) {
            if (Object.keys(localStorageFeatureFlags).length > 0) {
                const confirmed = window.confirm('You have opened a link with URL feature flag overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.');
                if (!confirmed) return;
            }
            setLocalStorageFeatureFlags(paramOverrides);
        }
    }, []);
    const reset = useCallback(()=>{
        setLocalStorageFeatureFlags({});
    }, [
        setLocalStorageFeatureFlags
    ]);
    const mergedFlags = isChronoAdmin ? {
        ...flags,
        ...envConfigLocalOverrides,
        ...localStorageFeatureFlags
    } : flags;
    return /*#__PURE__*/ jsx(FeatureFlagContext.Provider, {
        value: {
            isLoading: fetching,
            originalFeatureFlags: flags,
            featureFlags: mergedFlags,
            setFeatureFlags: setLocalStorageFeatureFlags,
            reset
        },
        children: props.children
    });
}
function useFeatureFlagsState() {
    const { featureFlags, setFeatureFlags, originalFeatureFlags, reset } = useContext(FeatureFlagContext);
    return {
        originalFeatureFlags,
        featureFlags,
        setFeatureFlags,
        reset
    };
}
function useFeatureFlags() {
    const { featureFlags } = useFeatureFlagsState();
    return featureFlags;
}
function useIsFeatureFlagsLoading() {
    const featureFlagsContext = useContext(FeatureFlagContext);
    return featureFlagsContext.isLoading;
}
function FeatureFlagGate(props) {
    const { condition, fallback, children } = props;
    const { featureFlags } = useFeatureFlagsState();
    const isAllowed = condition({
        featureFlags
    });
    if (!isAllowed) return fallback;
    return children;
}
export { DEFAULT_FEATURE_FLAGS, FeatureFlagContext, FeatureFlagContextProvider, FeatureFlagGate, useFeatureFlags, useFeatureFlagsState, useIsFeatureFlagsLoading };

//# sourceMappingURL=FeatureFlags.js.map